import React from 'react';

/**
 * Skeleton loader for service detail page
 */
const ServiceDetailSkeleton = () => {
  return (
    <div className="min-h-screen bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Service Name Skeleton */}
        <div className="h-8 bg-gray-200 rounded-lg w-3/4 mb-6 animate-pulse">
          <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-8 lg:gap-12">
          {/* Left Column */}
          <div className="space-y-8">
            {/* Service Image Skeleton */}
            <div className="relative">
              <div className="aspect-[4/3] sm:aspect-[16/10] bg-gray-200 rounded-lg overflow-hidden animate-pulse">
                <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
              </div>
              {/* Badges Skeleton */}
              <div className="absolute top-4 left-4 flex flex-col gap-2">
                <div className="h-6 w-24 bg-gray-300 rounded-full animate-pulse">
                  <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
                </div>
              </div>
            </div>

            {/* Location, Rating Skeleton */}
            <div className="pb-6 border-b border-gray-200">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <div className="h-6 w-32 bg-gray-200 rounded-full animate-pulse">
                  <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
                </div>
                <div className="h-6 w-40 bg-gray-200 rounded-full animate-pulse">
                  <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
                </div>
              </div>
              <div className="flex items-center justify-between gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className="h-5 w-5 bg-gray-200 rounded animate-pulse">
                        <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
                      </div>
                    ))}
                  </div>
                  <div className="h-4 w-12 bg-gray-200 rounded animate-pulse">
                    <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="h-4 w-16 bg-gray-200 rounded animate-pulse">
                    <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
                  </div>
                  <div className="h-4 w-12 bg-gray-200 rounded animate-pulse">
                    <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
                  </div>
                </div>
              </div>
              <div className="h-4 w-48 bg-gray-200 rounded animate-pulse">
                <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
              </div>
            </div>

            {/* Description Skeleton */}
            <div className="pb-6 border-b border-gray-200 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-full animate-pulse">
                <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
              </div>
              <div className="h-4 bg-gray-200 rounded w-11/12 animate-pulse">
                <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
              </div>
              <div className="h-4 bg-gray-200 rounded w-5/6 animate-pulse">
                <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
              </div>
            </div>

            {/* Key Details Skeleton */}
            <div className="pb-6 border-b border-gray-200">
              <div className="h-6 bg-gray-200 rounded w-48 mb-6 animate-pulse">
                <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="space-y-2">
                    <div className="h-5 w-32 bg-gray-200 rounded animate-pulse">
                      <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
                    </div>
                    <div className="h-6 w-24 bg-gray-200 rounded animate-pulse">
                      <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Tabs Navigation Skeleton */}
            <div className="border-b border-gray-200 mb-6">
              <div className="flex gap-4 pb-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-6 w-24 bg-gray-200 rounded animate-pulse">
                    <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
                  </div>
                ))}
              </div>
            </div>

            {/* Tab Content Skeleton */}
            <div className="space-y-6">
              <div className="h-6 bg-gray-200 rounded w-40 mb-6 animate-pulse">
                <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
              </div>
              <div className="space-y-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex items-center justify-between py-3 border-b border-gray-100">
                    <div className="h-5 w-32 bg-gray-200 rounded animate-pulse">
                      <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
                    </div>
                    <div className="h-5 w-24 bg-gray-200 rounded animate-pulse">
                      <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Booking Widget Skeleton */}
          <div className="hidden lg:block">
            <div className="sticky top-20">
              <div className="border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="mb-6">
                  <div className="h-8 bg-gray-200 rounded w-32 mb-4 animate-pulse">
                    <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
                  </div>
                  <div className="h-12 bg-gray-200 rounded-lg w-full mb-3 animate-pulse">
                    <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
                  </div>
                  <div className="h-4 bg-gray-200 rounded w-40 mx-auto animate-pulse">
                    <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
                  </div>
                </div>
                <div className="pt-6 border-t border-gray-200 space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center justify-between">
                      <div className="h-4 w-24 bg-gray-200 rounded animate-pulse">
                        <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
                      </div>
                      <div className="h-4 w-20 bg-gray-200 rounded animate-pulse">
                        <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetailSkeleton;

